/**
 * The OAuth 2.1 consent and grant surface of GoTrue, as the Console reads it
 * (#2443 slice 4). GoTrue is the AUTHORIZATION SERVER and the only authority for
 * clients, grants, sessions and revocation — nothing here stores any of them.
 *
 * Shapes were MEASURED on the real GoTrue 2.195.0 binary (#2443 80701/80725):
 *   GET  /oauth/authorizations/{id}   pending → {authorization_id, client:{id,name}, redirect_uri, scope, user}
 *                                     already consented → {redirect_url}
 *   POST /oauth/authorizations/{id}/consent {action:'approve'|'deny'} → {redirect_url}
 *   GET  /user/oauth/grants           → [{client:{id,name}, granted_at, scopes}]
 *   DELETE /user/oauth/grants?client_id=…  (deletes the client's sessions for this user)
 *
 * Three rules every reader below keeps:
 *   · the authorization is BOUND TO THE ID THAT WAS ASKED FOR — a response naming
 *     another transaction is refused, so nothing in the page's own query string
 *     (client, redirect, resource, scope) can substitute for GoTrue's answer;
 *   · a redirect is followed only when it goes where the client REGISTERED
 *     (same origin and path as the transaction's redirect_uri) — GoTrue appends
 *     `code` and `state`, and the Console never reads, stores or shows either;
 *   · every error is fixed text. A response body is never echoed into a message.
 */
import { AuthError } from './session.js'

export interface OAuthClientSummary {
  /** GoTrue's public client id. Never a secret: a public client has none. */
  id: string
  /** The label the operator registered. Shown to the person, never trusted as identity. */
  name: string
}

export type OAuthAuthorization =
  | { kind: 'pending'; authorizationId: string; client: OAuthClientSummary; redirectUri: string; scopes: string[] }
  /** GoTrue auto-approved a repeat request for a client this person already consented to.
   *  Only navigability is checked here: GoTrue returns no registered redirect to
   *  compare with (see ConsentPage — ROOT ruling, and the slice-5 rehearsal). */
  | { kind: 'decided'; redirectUrl: string }

export interface OAuthGrant {
  client: OAuthClientSummary
  grantedAt: string | null
  scopes: string[]
}

/** GoTrue's authorization ids are opaque tokens of URL-safe characters. Anything
 *  else is refused BEFORE a request is made — it cannot become a path segment. */
const AUTHORIZATION_ID = /^[A-Za-z0-9_-]{8,128}$/

export function oauthAuthorizationId(raw: string | null | undefined): string {
  if (typeof raw !== 'string' || !AUTHORIZATION_ID.test(raw)) {
    throw new AuthError('This authorization request is missing or malformed.')
  }
  return raw
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
const nonEmpty = (v: unknown): v is string => typeof v === 'string' && v.trim() !== ''

function readClient(v: unknown): OAuthClientSummary | null {
  if (!isObject(v)) return null
  const id = v.id ?? v.client_id
  const name = v.name ?? v.client_name
  return nonEmpty(id) && nonEmpty(name) ? { id, name } : null
}

function readScopes(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter(nonEmpty)
  if (typeof v === 'string') return v.split(/\s+/).filter(Boolean)
  return []
}

/** A URL the browser may be sent to at all: https, or http on a loopback host. */
export function isNavigableRedirect(url: string): boolean {
  let u: URL
  try { u = new URL(url) } catch { return false }
  if (u.protocol === 'https:') return true
  return u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '[::1]')
}

/** Does GoTrue's redirect go where the client REGISTERED? Origin and path must
 *  match exactly; GoTrue adds only its own query (`code`, `state`, `error`). */
export function redirectMatchesRegistered(redirectUrl: string, registered: string): boolean {
  let a: URL, b: URL
  try { a = new URL(redirectUrl); b = new URL(registered) } catch { return false }
  return isNavigableRedirect(redirectUrl) && a.origin === b.origin && a.pathname === b.pathname
}

/** RFC 9207 issuer identification (#2512): the deployment advertises
 *  `authorization_response_iss_parameter_supported`, so EVERY authorization
 *  response it sends must carry `iss` equal to its issuer — clients such as
 *  ChatGPT and Claude reject one without it. GoTrue does not add it; the
 *  Console consent page sends the success and deny responses, so it adds it
 *  here (the relay's OAuth front adds it to the error redirects GoTrue sends).
 *  A pre-existing `iss` is REPLACED, never duplicated. Null for an unusable URL. */
export function withAuthorizationIssuer(redirectUrl: string, issuer: string): string | null {
  let u: URL
  try { u = new URL(redirectUrl) } catch { return null }
  if (!isNavigableRedirect(redirectUrl)) return null
  u.searchParams.delete('iss')
  u.searchParams.append('iss', issuer)
  return u.toString()
}

export function readOAuthAuthorization(body: unknown, expectedId: string): OAuthAuthorization | null {
  if (!isObject(body)) return null
  if (nonEmpty(body.redirect_url) && body.client === undefined) {
    return isNavigableRedirect(body.redirect_url) ? { kind: 'decided', redirectUrl: body.redirect_url } : null
  }
  const client = readClient(body.client)
  if (body.authorization_id !== expectedId || !client || !nonEmpty(body.redirect_uri)) return null
  if (!isNavigableRedirect(body.redirect_uri)) return null
  return { kind: 'pending', authorizationId: expectedId, client, redirectUri: body.redirect_uri, scopes: readScopes(body.scope) }
}

export function readOAuthDecision(body: unknown): string | null {
  if (!isObject(body)) return null
  const url = body.redirect_url ?? body.redirect_to
  return nonEmpty(url) && isNavigableRedirect(url) ? url : null
}

export function readOAuthGrants(body: unknown): OAuthGrant[] | null {
  const list = Array.isArray(body) ? body : isObject(body) && Array.isArray(body.grants) ? body.grants : null
  if (!list) return null
  const out: OAuthGrant[] = []
  for (const g of list) {
    if (!isObject(g)) return null
    const client = readClient(g.client)
    if (!client) return null
    out.push({ client, grantedAt: nonEmpty(g.granted_at) ? g.granted_at : null, scopes: readScopes(g.scopes ?? g.scope) })
  }
  return out
}

/** A client id destined for a query string: bounded, printable, no separators. */
export function oauthClientId(raw: string): string {
  if (!/^[A-Za-z0-9._:-]{1,128}$/.test(raw)) throw new AuthError('That app could not be identified.')
  return raw
}
